import React from 'react'

import { MutableAppMessageConditions } from '@pushly/models/lib/structs/app-messages/app-message-conditions'
import { AppMessageConditionType } from '@pushly/models/lib/enums/app-messages/app-message-condition-type'
import { getClassNames } from '../../../_utils/classnames'
import { titleCase } from '@pushly/models/lib/utils/change-case'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { PropertiesOf } from '@pushly/models/lib/types'
import { RelativeDate } from '@pushly/models/lib/structs/relative-date'
import { convertSeconds, convertToSeconds } from '../../../_utils/time'
import { LIFETIME_METRICS, SESSION_METRICS_FILTERED } from '../constants'
import { ConditionDuration, SessionTriggerTypes } from '../types'
// import './styles/app-message-conditions-builder.scss'

import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { useState } from 'react'
import { AppMessageSettingRow } from './app-message-setting-row'
import { Input, Radio, Select, Skeleton, Space } from 'antd'

type AppMessageConditionsBuilderProps = {}

const SESSION_TRIGGER_LABELS: Record<SessionTriggerTypes, string> = {
    [SessionTriggerTypes.IMMEDIATE]: 'Immediately',
    [SessionTriggerTypes.SESSION_START]: 'On session start',
    [SessionTriggerTypes.TIME_IN_APP]: 'After time in app',
    [SessionTriggerTypes.CUSTOM]: 'Custom trigger',
}

/**
 * What each trigger means, in one line, shown under the choice rather than in a
 * tooltip: the trigger decides whether anyone sees the message at all, so its
 * meaning should not be something the reader has to go looking for.
 */
const SESSION_TRIGGER_DESCRIPTIONS: Record<SessionTriggerTypes, string> = {
    [SessionTriggerTypes.IMMEDIATE]: 'Shows immediately or the next time the app opens if it is currently closed.',
    [SessionTriggerTypes.SESSION_START]:
        'Shows when a new session begins — when the user opens the app after being away for long enough, or after a cold start.',
    [SessionTriggerTypes.TIME_IN_APP]: 'Shows once the user has spent the configured amount of time in the app.',
    [SessionTriggerTypes.CUSTOM]: 'Shows once the defined custom trigger condition is met.',
}

const SESSION_TRIGGER_OPTIONS = (Object.values(SessionTriggerTypes) as SessionTriggerTypes[]).map((value) => ({
    value,
    label: SESSION_TRIGGER_LABELS[value],
}))

const mapConditionToSessionTriggerType = (condition: AppMessageConditionType): SessionTriggerTypes | undefined => {
    switch (condition) {
        case AppMessageConditionType.SESSION_START:
            return SessionTriggerTypes.SESSION_START
        case AppMessageConditionType.IMMEDIATE:
            return SessionTriggerTypes.IMMEDIATE
        case AppMessageConditionType.SESSION_TIME_IN_APP:
        case AppMessageConditionType.LIFETIME_TIME_IN_APP:
            return SessionTriggerTypes.TIME_IN_APP
        case AppMessageConditionType.CUSTOM:
            return SessionTriggerTypes.CUSTOM
        default:
            return undefined
    }
}

export const AppMessageConditionsBuilder: React.FC<
    React.PropsWithChildren<React.PropsWithChildren<AppMessageConditionsBuilderProps>>
> = (props) => {
    const [context, dispatch] = useAppMessageBuilderContext()
    const { variants, selectedVariantIdx } = context

    const value = variants[selectedVariantIdx].schedules![0].conditions

    const localValue = value ?? new MutableAppMessageConditions()
    const [displayMetricOptions, setDisplayMetricOptions] = useState(SESSION_METRICS_FILTERED)
    const [plainTimeInAppSeconds, setPlainTimeInAppSeconds] = useState(
        !!localValue.timeInApp
            ? convertSeconds(localValue.timeInApp.intervalSeconds, localValue.timeInApp.displayMetric)
            : 60,
    )

    const handleConditionsChange = (change: Partial<MutableAppMessageConditions>) => {
        dispatch({ type: 'patch', entity: 'conditions', data: localValue.clone(change) })
    }

    const handleTimeInAppChange = (change: PropertiesOf<RelativeDate>) => {
        const timeInApp = RelativeDate.from(change as RelativeDate)
        handleConditionsChange({ timeInApp })
    }

    /**
     * Reset display options when type is updated, because discrepancy between options also default metric selected when not available
     */
    React.useEffect(() => {
        if (localValue.type === AppMessageConditionType.SESSION_TIME_IN_APP) {
            if (!SESSION_METRICS_FILTERED.includes(localValue.timeInApp!.displayMetric)) {
                handleTimeInAppChange({
                    ...localValue.timeInApp!,
                    displayMetric: RelativeDateDisplayMetric.MINUTES,
                })
            }
            setDisplayMetricOptions(SESSION_METRICS_FILTERED)
        } else if (localValue.type === AppMessageConditionType.LIFETIME_TIME_IN_APP) {
            setDisplayMetricOptions(LIFETIME_METRICS)
        }
    }, [value])

    return (
        <div className={getClassNames('app-message-conditions-builder')}>
            <Skeleton loading={context.loading} active={true} avatar={true} title={false}>
                <AppMessageSettingRow>
                    <Radio.Group
                        aria-label="Trigger"
                        className="trigger-type-options"
                        buttonStyle="solid"
                        optionType="button"
                        size="small"
                        options={SESSION_TRIGGER_OPTIONS}
                        value={mapConditionToSessionTriggerType(localValue.type) ?? SessionTriggerTypes.IMMEDIATE}
                        disabled={context.disabled}
                        onChange={(ev) => {
                            const triggerType = ev.target.value
                            let update: any = {}
                            switch (triggerType) {
                                case SessionTriggerTypes.SESSION_START:
                                    update = {
                                        type: AppMessageConditionType.SESSION_START,
                                        timeInApp: {
                                            intervalSeconds: 60,
                                            displayMetric: RelativeDateDisplayMetric.SECONDS,
                                        },
                                    }
                                    break
                                case SessionTriggerTypes.TIME_IN_APP:
                                    // Session type defaulted for time-in-app config below
                                    update = {
                                        type: AppMessageConditionType.SESSION_TIME_IN_APP,
                                        timeInApp: undefined,
                                    }
                                    break
                                case SessionTriggerTypes.CUSTOM:
                                    update = { type: AppMessageConditionType.CUSTOM, timeInApp: undefined }
                                    break
                                case SessionTriggerTypes.IMMEDIATE:
                                    update = { type: AppMessageConditionType.IMMEDIATE, timeInApp: undefined }
                                    break
                            }

                            if (update.type === AppMessageConditionType.SESSION_TIME_IN_APP) {
                                update.timeInApp = {
                                    intervalSeconds: 60,
                                    displayMetric: RelativeDateDisplayMetric.SECONDS,
                                }
                            } else {
                                update.timeInApp = undefined
                            }

                            handleConditionsChange(update)
                        }}
                    />
                    <span className="trigger-type-description">
                        {
                            SESSION_TRIGGER_DESCRIPTIONS[
                                mapConditionToSessionTriggerType(localValue.type) ?? SessionTriggerTypes.IMMEDIATE
                            ]
                        }
                    </span>
                </AppMessageSettingRow>

                {(value?.type === AppMessageConditionType.SESSION_TIME_IN_APP ||
                    value?.type === AppMessageConditionType.LIFETIME_TIME_IN_APP) && (
                    <AppMessageSettingRow>
                        <Space.Compact>
                            <Input
                                id="amb-time-in-app-value"
                                aria-label="Time in app duration"
                                type="number"
                                value={
                                    localValue.timeInApp && localValue.timeInApp.intervalSeconds
                                        ? (plainTimeInAppSeconds ?? 1)
                                        : undefined
                                }
                                min={1}
                                max={365}
                                disabled={context.disabled}
                                onChange={(ev) => {
                                    const val = parseInt(ev.target.value.trim(), 10)
                                    const intervalSeconds = convertToSeconds(
                                        val,
                                        localValue.timeInApp?.displayMetric ?? RelativeDateDisplayMetric.SECONDS,
                                    )
                                    setPlainTimeInAppSeconds(val)
                                    handleTimeInAppChange({
                                        intervalSeconds,
                                        displayMetric:
                                            localValue.timeInApp?.displayMetric ?? RelativeDateDisplayMetric.SECONDS,
                                    })
                                }}
                            />
                            <Select<RelativeDateDisplayMetric>
                                aria-label="Time in app metric"
                                value={localValue.timeInApp?.displayMetric ?? RelativeDateDisplayMetric.SECONDS}
                                options={displayMetricOptions.map((opt) => ({
                                    value: opt,
                                    label: titleCase(opt),
                                }))}
                                disabled={context.disabled}
                                onChange={(metric) => {
                                    handleTimeInAppChange({
                                        intervalSeconds: convertToSeconds(plainTimeInAppSeconds!, metric),
                                        displayMetric: metric,
                                    })
                                }}
                                style={{ width: 120 }}
                            />
                        </Space.Compact>

                        {/* Whether the duration is counted within one session or
                            across all of them: two named options rather than a
                            labelled setting, since the names say it. */}
                        <Radio.Group
                            aria-label="Duration scope"
                            buttonStyle="solid"
                            optionType="button"
                            size="small"
                            disabled={context.disabled}
                            value={localValue.type?.split('_', 1)[0] ?? ConditionDuration.SESSION}
                            onChange={(ev) => {
                                handleConditionsChange({
                                    type: AppMessageConditionType[ev.target.value + '_TIME_IN_APP'],
                                })
                            }}
                            options={[
                                { label: 'This session', value: ConditionDuration.SESSION },
                                { label: 'Lifetime', value: ConditionDuration.LIFETIME },
                            ]}
                        />
                    </AppMessageSettingRow>
                )}

                {value?.type === AppMessageConditionType.CUSTOM && (
                    <AppMessageSettingRow
                        label="Custom trigger"
                        info="Custom Triggers are represented with an evaluating key and the qualitative value of that key. Ex: Key: app_opened, Value: 5"
                    >
                        <Space.Compact className="custom-trigger-fields">
                            <Space.Compact>
                                <Space.Addon>
                                    <span>Key</span>
                                </Space.Addon>
                                <Input
                                    aria-label="Custom trigger key"
                                    value={localValue.triggers?.key}
                                    disabled={context.disabled}
                                    onChange={(ev) => {
                                        const key = ev.target.value.trim()
                                        handleConditionsChange({ triggers: { ...localValue.triggers, key } })
                                    }}
                                    placeholder="app_opened"
                                />
                            </Space.Compact>

                            <Space.Compact>
                                <Space.Addon>
                                    <span>Value</span>
                                </Space.Addon>
                                <Input
                                    aria-label="Custom trigger value"
                                    value={localValue.triggers?.value}
                                    disabled={context.disabled}
                                    onChange={(ev) => {
                                        const val = ev.target.value.trim()
                                        handleConditionsChange({ triggers: { ...localValue.triggers, value: val } })
                                    }}
                                    placeholder="5"
                                />
                            </Space.Compact>
                        </Space.Compact>
                    </AppMessageSettingRow>
                )}
            </Skeleton>
        </div>
    )
}
